/**
 * Whether a newer shell is waiting, and the one action that applies it (D137).
 *
 * **The cache headers were never the problem.** `infra/web.ts` serves the hashed bundles
 * `immutable` and `sw.js`/`registerSW.js`/`manifest.webmanifest`/the icons `no-store`, verified
 * against the live `dev` distribution. Nothing stale can be *served*. Two things were missing:
 * anything that **asks**, and anywhere for the answer to **go**.
 *
 * `vite-plugin-pwa` emits a registration script that is one line long — a bare
 * `navigator.serviceWorker.register('/sw.js')` behind the `load` event, with no `update()` and no
 * periodic check. So a new `sw.js` was fetched only on a real page load, and **an installed
 * standalone app resumed from the app switcher fires no `load` event.** D132 then removed the
 * document scroll, which costs pull-to-refresh, so the resident had no gesture to force one
 * either. A resident could hold an arbitrarily old shell indefinitely, and no amount of file
 * hashing changes that: the hashes are right, the headers are right, and nothing was asking.
 *
 * **Nothing here applies an update on its own.** `vite.config.ts` sets `registerType: 'prompt'`,
 * so a newly installed worker *waits* rather than claiming the open page. This module notices it
 * waiting, reports that, and applies it only when `applyShellUpdate()` is called — which happens
 * from one place, the Update sheet's button. A gate-control app that reloaded itself unprompted
 * could do it inside D134's confirm window, at the gate, with the resident watching a boot screen
 * instead of *Opening…*.
 */

/** What the shell knows about a newer version of itself. */
export interface ShellUpdateState {
  /** A worker has installed and is waiting. The sidebar dot and the sheet's button follow this. */
  readonly available: boolean
  /** `applyShellUpdate()` has been called and the page is on its way out. */
  readonly applying: boolean
}

const IDLE: ShellUpdateState = { available: false, applying: false }

/**
 * Floor between checks, across every trigger below.
 *
 * `visibilitychange` fires on every app switch, and iOS residents switch apps a lot. The check
 * itself is one conditional GET of a ~2 KB `no-store` URL, so the cost of being wrong in this
 * direction is a few hundred bytes; the cost of being wrong in the other is the staleness this
 * module exists to end. A minute is short enough that a resume after any real interval checks,
 * and long enough that flipping between two apps does not.
 */
const MIN_INTERVAL_MS = 60_000

/**
 * How long `applyShellUpdate()` waits for the new worker to take over before reloading anyway.
 *
 * The reload is not conditional on the handover — see `applyShellUpdate()`. This only decides how
 * long the button may sit in its pressed state first.
 */
const HANDOVER_TIMEOUT_MS = 3_000

let state: ShellUpdateState = IDLE
const listeners = new Set<() => void>()

function publish(next: ShellUpdateState): void {
  if (next.available === state.available && next.applying === state.applying) return
  state = next
  for (const listener of listeners) listener()
}

/** `useSyncExternalStore`'s two halves. `getShellUpdateState` must return a stable reference. */
export function subscribeToShellUpdate(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function getShellUpdateState(): ShellUpdateState {
  return state
}

/**
 * Test seam — return to "nothing waiting" between cases.
 *
 * **It notifies rather than dropping the listeners**, which the first version did. Clearing the
 * set silently unsubscribes every mounted component, so the store keeps updating and the UI never
 * moves again — a dot stuck on with `available: false` underneath it. Harmless in a test that
 * unmounts straight after, and exactly the shape of bug this surface is least able to show you.
 */
export function __resetShellUpdateState(): void {
  publish(IDLE)
}

/**
 * Applies the waiting shell: tell it to stop waiting, then reload.
 *
 * **The reload is unconditional, and that is deliberate.** `skipWaiting` is delivered by
 * `postMessage`, which only works if the generated `sw.js` carries Workbox's `SKIP_WAITING`
 * listener — true for `generateSW` with `skipWaiting: false`, but a fact about a build artifact
 * rather than about this code, and a resident tapping *Update* must not be left looking at a
 * button that did nothing. So the handover is raced against a timeout and the page reloads either
 * way: with the handover the new shell is already active, and without it the reload is an ordinary
 * one that releases the old worker and lets the waiting one activate on the following launch. The
 * failure mode degrades to "one launch later", never to "nothing happened".
 */
export async function applyShellUpdate(): Promise<void> {
  if (state.applying) return
  publish({ available: state.available, applying: true })

  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.getRegistration().catch(() => undefined)
    // `!= null`, not `!== undefined`: `ServiceWorkerRegistration.waiting` is `ServiceWorker |
    // null`, so the optional chain yields all three and only the loose check excludes both.
    const waiting = registration?.waiting
    if (waiting != null) {
      const handover = new Promise<void>((resolve) => {
        const done = (): void => {
          navigator.serviceWorker.removeEventListener('controllerchange', done)
          resolve()
        }
        navigator.serviceWorker.addEventListener('controllerchange', done)
        setTimeout(done, HANDOVER_TIMEOUT_MS)
      })
      waiting.postMessage({ type: 'SKIP_WAITING' })
      await handover
    }
  }

  window.location.reload()
}

/**
 * Installs the check and returns its teardown. Safe to call once at startup — it registers
 * nothing and races nothing, because `registerSW.js` owns registration and this only ever reads
 * back what that produced.
 */
export function installShellUpdateCheck(): () => void {
  if (!('serviceWorker' in navigator)) return () => undefined
  const container = navigator.serviceWorker

  let lastCheckAt = 0
  let inFlight = false
  let installed = true

  function report(registration: ServiceWorkerRegistration | undefined): void {
    if (!installed) return
    publish({ available: registration?.waiting != null, applying: state.applying })
  }

  /**
   * Watch this registration for a worker that finishes installing later.
   *
   * `update()` resolves when the *check* completes, not when a newly found worker has installed —
   * so reading `.waiting` off the resolved registration alone reports `false` for a download that
   * is still in flight, and nothing would ever correct it. `updatefound` plus the installing
   * worker's `statechange` is what turns "found" into "waiting".
   */
  function watch(registration: ServiceWorkerRegistration): void {
    registration.addEventListener('updatefound', () => {
      const installing = registration.installing
      if (installing === null) return
      installing.addEventListener('statechange', () => report(registration))
    })
  }

  const watched = new WeakSet<ServiceWorkerRegistration>()

  function check(): void {
    // Not on the way *out*. `visibilitychange` fires in both directions and half its firings are
    // a backgrounding, where the answer cannot be acted on and the app may be about to be frozen.
    if (!installed || document.visibilityState !== 'visible') return

    if (inFlight || Date.now() - lastCheckAt < MIN_INTERVAL_MS) return
    inFlight = true

    void container
      .getRegistration()
      // `undefined` happens only on the very first load of all, before `registerSW.js` has run on
      // `load`; every later launch resolves the registration a previous session left behind, which
      // is what makes the startup call worth making. **The window is consumed here rather than
      // above** so that first load does not silently disable checking for the following minute.
      .then(async (registration) => {
        if (registration === undefined) return
        lastCheckAt = Date.now()
        if (!watched.has(registration)) {
          watched.add(registration)
          watch(registration)
        }
        // Report first: a worker that installed during a previous session is already waiting, and
        // the resident should see the dot without waiting for this round trip to finish.
        report(registration)
        await registration.update()
        report(registration)
      })
      // **Swallowed on purpose.** `update()` rejects when `sw.js` cannot be fetched, which is
      // every offline resume — and an unhandled rejection there would be a startup error on the
      // one path the shell is meant to survive silently (D129's posture: a network failure is
      // offline-read-only, never a session event). There is nothing to report and nothing to
      // retry; the next resume asks again.
      .catch(() => undefined)
      .finally(() => {
        inFlight = false
      })
  }

  document.addEventListener('visibilitychange', check)
  // A bfcache restore, which runs no module code and fires no `load`. `persisted` is the filter:
  // a `pageshow` with `persisted === false` *is* a real load, and `register()` has just checked.
  const onPageShow = (event: PageTransitionEvent): void => {
    if (event.persisted) check()
  }
  window.addEventListener('pageshow', onPageShow)
  // The first read: `registerSW.js` registers on `load`, so at startup there may already be a
  // worker waiting from a previous session. This costs no network round trip — `check()`'s own
  // guard skips the `update()` when the registration is not there yet.
  check()

  return () => {
    installed = false
    document.removeEventListener('visibilitychange', check)
    window.removeEventListener('pageshow', onPageShow)
  }
}
